import { useMutation, useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Loading } from '../ui/Page';
import { getTemplate, listTemplates, type PageContent, type TemplateKind } from './pages-api';

/**
 * Somewhere to start, offered only while there is nothing to lose.
 *
 * Shown on an empty document and nowhere else: a template replaces everything, and a control
 * that silently discards somebody's work is a control that should not be reachable once they
 * have any. Starting from a blank page stays one click away, because most people who know what
 * they want find a template slower than typing.
 */
export function TemplatePicker({
  kind,
  onPick,
  onError,
}: {
  kind: TemplateKind;
  onPick: (content: PageContent) => void;
  onError?: (message: string) => void;
}): React.JSX.Element | null {
  const templates = useQuery({
    queryKey: ['templates', kind],
    queryFn: () => listTemplates(kind),
    staleTime: 60 * 60 * 1000,
  });

  const pick = useMutation({
    mutationFn: (key: string) => getTemplate(key),
    onSuccess: (template) => onPick(template.content),
    onError: (caught: unknown) => onError?.(messageFor(caught)),
  });

  if (templates.isLoading) return <Loading />;

  const available = templates.data?.templates ?? [];

  if (available.length === 0) return null;

  return (
    <div className="templates">
      <p className="muted small">{t('templates.start')}</p>

      <ul className="templates__list">
        {available.map((template) => (
          <li key={template.key}>
            <button
              type="button"
              className="templates__card"
              disabled={pick.isPending}
              onClick={() => pick.mutate(template.key)}
            >
              <span className="strong">{template.name}</span>
              <span className="muted small">{template.description}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
